import { useEffect, useState } from "react";
import { Users, MessageSquare, Heart, Send, Crown, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CommunityPage() {
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [activeClub, setActiveClub] = useState("saving");
  const [posts, setPosts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [commentBody, setCommentBody] = useState("");
  const [comments, setComments] = useState({});

  const loadClubs = async () => {
    try { const { data } = await api.get("/community/clubs"); setClubs(data); } catch {}
  };
  const loadPosts = async (clubId) => {
    try { const { data } = await api.get(`/community/clubs/${clubId}/posts`); setPosts(data); } catch {}
  };
  const loadLb = async () => {
    try { const { data } = await api.get("/community/leaderboard"); setLeaderboard(data); } catch {}
  };
  const loadComments = async (postId) => {
    try { const { data } = await api.get(`/community/posts/${postId}/comments`); setComments((c) => ({ ...c, [postId]: data })); } catch {}
  };

  useEffect(() => { loadClubs(); loadLb(); }, []);
  useEffect(() => { loadPosts(activeClub); }, [activeClub]);

  const submitPost = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      await api.post("/community/posts", { club_id: activeClub, title, content });
      setTitle(""); setContent("");
      toast.success("Posted");
      loadPosts(activeClub); loadClubs(); loadLb();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setPosting(false); }
  };

  const submitComment = async (postId) => {
    if (!commentBody.trim()) return;
    try {
      await api.post(`/community/posts/${postId}/comments`, { content: commentBody });
      setCommentBody("");
      loadComments(postId);
      loadPosts(activeClub);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const like = async (postId) => {
    try { await api.post(`/community/posts/${postId}/like`); loadPosts(activeClub); } catch {}
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Community</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Zuntria Clubs</h1>
      <p className="mt-2 text-sm text-muted-foreground">Share wins, swap ideas, climb the leaderboard.</p>

      <Tabs value={activeClub} onValueChange={setActiveClub} className="mt-8">
        <TabsList data-testid="club-tabs">
          {clubs.map((c) => (
            <TabsTrigger key={c.id} value={c.id} data-testid={`tab-${c.id}`}>
              {c.name} <span className="ml-1 text-[10px] text-muted-foreground">({c.post_count})</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {clubs.map((c) => (
          <TabsContent key={c.id} value={c.id} className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr,260px]">
            <div className="space-y-4">
              {/* Composer */}
              <div className="rounded-2xl border p-4" style={{ borderColor: `${c.color}40`, background: `${c.color}08` }} data-testid={`composer-${c.id}`}>
                <div className="flex items-center gap-2"><Sparkles className="h-4 w-4" style={{ color: c.color }} /><span className="text-xs font-semibold uppercase tracking-wider" style={{ color: c.color }}>{c.tagline}</span></div>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="mt-3 h-10" data-testid={`post-title-${c.id}`} />
                <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={`Share something with the ${c.name}…`} className="mt-2 min-h-[80px]" data-testid={`post-body-${c.id}`} />
                <div className="mt-3 flex justify-end">
                  <Button onClick={submitPost} disabled={posting || !content.trim()} size="sm" data-testid={`post-submit-${c.id}`}>
                    <Send className="mr-1 h-3 w-3" /> Post
                  </Button>
                </div>
              </div>
              {posts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">No posts yet. Be the first 🚀</div>
              ) : posts.map((p) => (
                <div key={p.post_id} className="rounded-2xl border border-border bg-card p-5" data-testid={`post-${p.post_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{p.user_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                  {p.title && <h3 className="mt-2 font-display text-lg">{p.title}</h3>}
                  <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/90">{p.content}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <button onClick={() => like(p.post_id)} className="flex items-center gap-1 hover:text-rose-400" data-testid={`like-${p.post_id}`}><Heart className="h-3 w-3" /> {p.likes || 0}</button>
                    <button onClick={() => { const n = openComments === p.post_id ? null : p.post_id; setOpenComments(n); if (n) loadComments(p.post_id); }} className="flex items-center gap-1 hover:text-foreground" data-testid={`comment-toggle-${p.post_id}`}>
                      <MessageSquare className="h-3 w-3" /> {p.comment_count || 0}
                    </button>
                  </div>
                  {openComments === p.post_id && (
                    <div className="mt-4 space-y-3 border-t border-border pt-4" data-testid={`comments-${p.post_id}`}>
                      {(comments[p.post_id] || []).map((cm) => (
                        <div key={cm.comment_id} className="rounded-lg bg-background/60 p-3 text-sm">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{cm.user_name}</div>
                          <p className="mt-1">{cm.content}</p>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Reply…" className="h-9" data-testid={`comment-input-${p.post_id}`} />
                        <Button size="sm" onClick={() => submitComment(p.post_id)} data-testid={`comment-submit-${p.post_id}`}>Send</Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <aside className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">About</span></div>
                <p className="mt-2 text-sm">{c.description}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{c.member_count} contributors</span>
                  <span>{c.post_count} posts</span>
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-2"><Crown className="h-4 w-4 text-accent" /><span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Top contributors (30d)</span></div>
                <div className="mt-3 space-y-2">
                  {leaderboard.length === 0 ? <div className="text-xs text-muted-foreground">No activity yet.</div> : leaderboard.slice(0, 5).map((r, i) => (
                    <div key={r.user_id} className="flex items-center justify-between rounded-md bg-background/50 px-2 py-1.5 text-xs" data-testid={`lb-${i}`}>
                      <span className="flex items-center gap-2"><span className="font-mono text-muted-foreground">{i + 1}</span> {r.user_name}</span>
                      <span className="font-display">{r.score} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
